import * as focusTrap from 'focus-trap'
import { addScrollEvent } from './scroll-event'

const mClasses = {
    menu: 'menu',
    toggle: 'menu__toggle',
    nav: 'menu__nav',
    navAnimating: 'menu__nav--animating',
    item: 'menu__item',
    itemTopLevel: 'menu__item--depth-0',
    subMenu: 'menu__sub-menu',
    subMenuToggle: 'menu__sub-menu-toggle',
    itemHasChildren: 'menu-item-has-children',
    bodyNoScroll: 'no-scroll',
}

type NavBarOpts = {
    scrollClass?: string
    breakpoint?: number
}

export class navBar {
    nav: HTMLElement
    toggler: HTMLButtonElement
    bar: HTMLElement
    hasChildren: NodeListOf<Element>
    navMenuLinks: NodeListOf<Element>
    breakpoint?: number
    mobile: boolean
    trap: focusTrap.FocusTrap

    constructor(wrapper: HTMLElement, { scrollClass, breakpoint }: NavBarOpts = {}) {
        this.nav = wrapper

        this.toggler = this.nav.querySelector(`.${mClasses.toggle}`)
        this.bar = this.nav.querySelector(`.${mClasses.nav}`)
        this.hasChildren = this.nav.querySelectorAll(`.${mClasses.itemHasChildren}`)
        this.navMenuLinks = this.bar.querySelectorAll('a')
        if (breakpoint) this.breakpoint = breakpoint
        this.mobile = window.innerWidth < this.breakpoint

        this.trap = focusTrap.createFocusTrap(this.nav, {
            clickOutsideDeactivates: false,
            escapeDeactivates: false,
            initialFocus: this.toggler,
            allowOutsideClick: true,
        })

        this.mobile = this.breakpoint ? window.innerWidth < this.breakpoint : true

        this.init()
        if (scrollClass) this.scrollEvent()
    }

    init() {
        this.clickInit()
        this.mobile ? this.mobileMenu() : this.desktopMenu()
        if (this.breakpoint) this.monitorSize()
        this.nav.addEventListener('keydown', this.keypress)
    }

    scrollEvent() {
        addScrollEvent({
            position: 300,
            onEnter: () => {
                this.nav.classList.add('scrolled')
            },
            onLeave: () => {
                this.nav.classList.remove('scrolled')
            },
        })
    }

    keypress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            let active = document.activeElement
            if (this.trap.active) {
                this.closeMenu()
            }
        }
    }

    mobileMenu() {
        this.bar.setAttribute('aria-hidden', 'true')
        this.toggler.setAttribute('aria-expanded', 'false')
        this.bar.style.display = 'none'

        this.hasChildren?.forEach((item) => {
            let subMenu = item.querySelector(`.${mClasses.subMenu}`)
            let subMenuLinks = item.querySelectorAll(`.${mClasses.subMenu} a`)

            subMenu && subMenu.setAttribute('aria-hidden', 'false')

            subMenuLinks?.forEach((link) => {
                link.setAttribute('tabindex', '0')
            })

            item.removeEventListener('mouseover', this.dropdownOpen)
            item.removeEventListener('mouseout', this.dropdownClose)
            item.removeEventListener('focusin', this.dropdownOpen)
            item.removeEventListener('focusout', this.dropdownClose)
        })
    }

    desktopMenu() {
        this.trap.deactivate()
        this.bar.setAttribute('aria-hidden', 'false')
        document.documentElement.style['overflow'] = 'auto'

        this.bar.style.display = 'block'

        this.hasChildren?.forEach((item) => {
            let subMenu = item.querySelector(`.${mClasses.subMenu}`)
            let toggler = item.querySelector(`.${mClasses.subMenuToggle}`)
            let subMenuLinks = item.querySelectorAll(`.${mClasses.subMenu} a`)

            subMenu?.setAttribute('aria-hidden', 'true')
            toggler?.setAttribute('aria-expanded', 'false')
            subMenuLinks?.forEach((link) => {
                link.setAttribute('tabindex', '-1')
            })

            item.addEventListener('mouseover', this.dropdownOpen)
            item.addEventListener('mouseout', this.dropdownClose)
            item.addEventListener('focusin', this.dropdownOpen)
            item.addEventListener('focusout', this.dropdownClose)
        })
    }

    monitorSize() {
        window.addEventListener('resize', () => {
            let w = window.innerWidth

            if (window.innerWidth < this.breakpoint) {
                if (!this.mobile) {
                    this.mobileMenu()
                    this.mobile = true
                }
            } else {
                if (this.mobile) {
                    this.mobile = false
                    this.desktopMenu()
                }
            }
        })
    }

    clickInit() {
        this.toggler.addEventListener('click', () => {
            if (this.bar.classList.contains(mClasses.navAnimating)) return
            if (this.trap.active) {
                this.closeMenu()
            } else {
                this.openMenu()
            }
        })
        document.body.addEventListener('click', (e) => {
            if (this.trap.active && !this.nav.contains(e.target as Node)) {
                this.closeMenu()
            }
        })
    }

    openMenu() {
        document.body.scrollTop = document.documentElement.scrollTop = 0
        document.documentElement.style['overflow'] = 'hidden'
        this.bar.classList.add(mClasses.navAnimating)
        this.toggler.setAttribute('aria-expanded', 'true')
        this.bar.setAttribute('aria-hidden', 'false')
        this.trap.activate()

        this.bar.style.display = 'block'
        let animation = this.bar.animate(
            [
                { transform: 'translateY(-100%)', opacity: '0' },
                { transform: 'none', opacity: '1' },
            ],
            { duration: 300 }
        )
        animation.onfinish = () => {
            this.bar.classList.remove(mClasses.navAnimating)
            animation.commitStyles()
        }
    }

    closeMenu() {
        this.bar.classList.add(mClasses.navAnimating)
        document.documentElement.style['overflow'] = 'auto'
        this.toggler.setAttribute('aria-expanded', 'false')
        this.bar.setAttribute('aria-hidden', 'true')

        let animation = this.bar.animate(
            [
                { transform: 'none', opacity: '1' },
                { transform: 'translateY(-100%)', opacity: '0' },
            ],
            { duration: 300 }
        )

        animation.onfinish = () => {
            this.bar.classList.remove(mClasses.navAnimating)
            animation.commitStyles()
            this.bar.style.display = 'none'
            this.trap.deactivate()
        }
    }

    // Desktop menu dropdown open & close
    dropdownOpen(e: Event) {
        let item = e.currentTarget as HTMLElement
        let subMenuToggle = item.querySelector(`.${mClasses.subMenuToggle}`)
        let subMenu = item.querySelector(`.${mClasses.subMenu}`)

        item.classList.add(`${mClasses.item}--open`)
        subMenu?.setAttribute('aria-hidden', 'false')
        subMenuToggle?.setAttribute('aria-expanded', 'true')

        subMenu?.querySelectorAll('a').forEach((link) => {
            link.setAttribute('tabIndex', '0')
        })
    }

    dropdownClose(e: Event) {
        let item = e.currentTarget as HTMLElement
        let subMenuToggle = item.querySelector(`.${mClasses.subMenuToggle}`)
        let subMenu = item.querySelector(`.${mClasses.subMenu}`)

        item.classList.remove(`${mClasses.item}--open`)

        subMenu?.setAttribute('aria-hidden', 'true')
        subMenuToggle?.setAttribute('aria-expanded', 'false')

        subMenu?.querySelectorAll('a').forEach((link) => {
            link.setAttribute('tabIndex', '-1')
        })
    }
}

export function initNav() {
    let menu = document.querySelector(`.${mClasses.menu}`) as HTMLElement
    if (menu) {
        new navBar(menu, { breakpoint: 1200 })
    }
}
